import { gql } from '@apollo/client';
import { useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import 'styled-components/macro';

import { usePrevious } from 'core/support/usePrevious';
import { AttemptsChallengeAttemptFragment } from 'types/graphql';

import { ReactComponent as AlertIcon } from './images/alert.svg';
import { ReactComponent as ToggleIcon } from './images/toggle.svg';

interface AttemptsContainerProps {
  hasAttempts: boolean;
  expanded: boolean;
}

const AttemptsContainer = styled.div<AttemptsContainerProps>`
  width: 100%;
  height: 40px;
  flex: 0;

  min-height: ${props => (props.expanded ? '220px' : '40px')};
  max-height: 350px;
  background-color: #252f42;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  box-sizing: border-box;

  ${props =>
    !props.hasAttempts &&
    `
    display: none;
  `}
`;

const AttemptsToggle = styled.div`
  font-family: 'Libre Franklin', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  color: #ffffff;
  padding-left: 15px;
  box-sizing: border-box;
  font-size: 13px;
  width: 100%;
  line-height: 40px;
  position: relative;
`;

const AttemptsList = styled.div`
  height: calc(100% - 40px);
  overflow-y: auto;
  cursor: default;
  padding-top: 10px;
`;

const AttemptItem = styled.span`
  display: block;
  color: rgba(255, 255, 255, 0.6);
  padding-left: 15px;
  font-family: 'Libre Franklin', sans-serif;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 30px;
  transition: opacity 0.2s ease-in-out;
  -moz-transition: opacity 0.2s ease-in-out;
  -webkit-transition: opacity 0.2s ease-in-out;

  :not(:first-child) {
    opacity: 0.3;
  }

  &:hover {
    opacity: 1;
  }
`;

const AttemptsNumber = styled.span`
  ${tw`bg-red`}
  border-radius: 5px;
  color: #fff;
  font-size: 12px;
  margin-right: 5px;
  text-align: center;
  padding: 2px 7px;

  span {
    line-height: 20px;
  }
`;

const AttemptHeader = styled.span`
  ${tw`text-red`}
  font-weight: 600;
  position: relative;
  padding-left: 30px;
  line-height: 20px;
  font-size: 16px;
`;

const AttemptMessage = styled.span`
  display: block;
  font-family: 'Libre Franklin', sans-serif;
  font-size: 14px;
  color: #fff;
  padding-left: 30px;
`;

export interface AttemptsLegacyProps {
  expanded: boolean;
  attempts: AttemptsChallengeAttemptFragment[];
  onToggleClick?(): void;
}

gql`
  fragment AttemptsChallengeAttempt on ChallengeAttempt {
    message
  }
`;

export const AttemptsLegacy = ({ expanded, attempts, onToggleClick }: AttemptsLegacyProps) => {
  const prevAttemptsLength = usePrevious(attempts.length) ?? attempts.length;

  useEffect(() => {
    if (onToggleClick && prevAttemptsLength < attempts.length && !expanded) {
      onToggleClick();
    }
  }, [prevAttemptsLength, attempts.length, expanded, onToggleClick]);

  return (
    <AttemptsContainer hasAttempts={attempts.length > 0} expanded={expanded}>
      <AttemptsToggle onClick={onToggleClick}>
        <AttemptsNumber>{attempts.length}</AttemptsNumber>
        Attempts
        <div tw="absolute top-[10px] right-2">
          <ToggleIcon tw="h-2" />
        </div>
      </AttemptsToggle>

      {expanded && (
        <AttemptsList>
          {attempts.map((attempt, index) => (
            <AttemptItem key={index}>
              <AttemptHeader>
                <AlertIcon tw="absolute left-[0px] [height: 20px]" />
                Incorrect solution {attempts.length - index}/{attempts.length}
              </AttemptHeader>

              <AttemptMessage>{attempt.message}</AttemptMessage>
            </AttemptItem>
          ))}
        </AttemptsList>
      )}
    </AttemptsContainer>
  );
};
